import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useContentProtection } from "@/hooks/useContentProtection";
import ProtectionOverlay from "@/components/ProtectionOverlay";
import { CheckCircle2, Lock, PlayCircle, MessageSquare, ChevronDown, ChevronUp, AlertTriangle, Award } from "lucide-react";

export default function CoursePlayer() {
  const { courseId } = useParams();
  const { devtoolsOpen } = useContentProtection(true);
  const [course, setCourse] = useState(null);
  const [enr, setEnr] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [exam, setExam] = useState(null);
  const [examType, setExamType] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [question, setQuestion] = useState("");
  const [sendingQ, setSendingQ] = useState(false);
  const [askMsg, setAskMsg] = useState("");

  const load = async () => {
    const [c, e] = await Promise.all([
      api.get(`/courses/${courseId}`),
      api.get(`/enrollments/course/${courseId}`).catch(() => ({ data: null })),
    ]);
    setCourse(c.data);
    setEnr(e.data);
    if (e.data?.initial_exam_done && !activeLesson && c.data.lessons?.length) {
      setActiveLesson(c.data.lessons[0]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const openExam = async (type) => {
    try {
      const { data } = await api.get(`/courses/${courseId}/exams/${type}`);
      setExam(data); setExamType(type); setAnswers({}); setResult(null);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const submitExam = async () => {
    const arr = exam.questions.map((_, i) => answers[i] ?? -1);
    try {
      const { data } = await api.post(`/courses/${courseId}/exams/${examType}/submit`, { answers: arr });
      setResult(data);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const markComplete = async () => {
    if (!activeLesson) return;
    try {
      await api.post(`/lessons/${activeLesson.id}/complete`);
      const nextIdx = course.lessons.findIndex((l) => l.id === activeLesson.id) + 1;
      load();
      if (nextIdx < course.lessons.length) setActiveLesson(course.lessons[nextIdx]);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const askProfessor = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setSendingQ(true); setAskMsg("");
    try {
      await api.post("/tickets", { course_id: courseId, lesson_id: activeLesson?.id, question_text: question });
      setAskMsg("Pregunta enviada. Recibirás la respuesta por email.");
      setQuestion("");
    } catch (e) {
      setAskMsg(formatApiError(e.response?.data?.detail));
    } finally { setSendingQ(false); }
  };

  if (!course) return <div className="p-10 text-marino">Cargando...</div>;
  if (!enr) return (
    <div className="max-w-2xl mx-auto p-10 text-center">
      <h1 className="font-heading font-bold text-2xl text-marino">No estás inscrito en este curso</h1>
      <Link to="/redeem" className="mt-4 inline-block bg-naranja text-white px-5 py-2 rounded-lg">Activar código</Link>
    </div>
  );

  const completed = new Set(enr.completed_lessons || []);
  const allDone = course.lessons?.length > 0 && course.lessons.every((l) => completed.has(l.id));

  // Exam view (modal-ish full section)
  if (exam) {
    return (
      <div className="max-w-3xl mx-auto p-6 py-10">
        <ProtectionOverlay visible={devtoolsOpen} />
        <div className="bg-white border border-gris/30 rounded-xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-heading font-black text-2xl text-marino">
              {examType === "initial" ? "Examen Diagnóstico" : "Examen Final"}
            </h1>
            <span className="text-sm text-marino/70">
              Intento {(exam.attempts_used || 0) + 1} de {exam.max_attempts}
            </span>
          </div>

          {result ? (
            <div className="text-center py-8">
              <div className={`text-6xl font-heading font-black ${result.passed ? "text-green-600" : "text-red-600"}`}>{result.score}%</div>
              <p className="mt-3 text-marino">{result.passed ? "¡Aprobaste!" : "No alcanzaste el puntaje mínimo"}</p>
              {examType === "final" && result.passed && (
                <div className="mt-4 text-marino/70 text-sm">Tu certificado ya está disponible en tu panel.</div>
              )}
              {examType === "final" && !result.passed && result.attempts_used < result.max_attempts && (
                <div className="mt-4 text-yellow-700 text-sm">Te queda {result.max_attempts - result.attempts_used} intento.</div>
              )}
              <button data-testid="exam-close" onClick={() => { setExam(null); setResult(null); }} className="mt-6 bg-naranja text-white px-6 py-3 rounded-lg font-medium">Volver al curso</button>
            </div>
          ) : (
            <>
              {examType === "final" && (
                <div className="bg-naranja/10 border border-naranja/30 rounded-lg p-3 mb-4 flex gap-2 text-sm">
                  <AlertTriangle className="w-5 h-5 text-naranja flex-shrink-0" />
                  <span>Advertencia: dispones de {exam.max_attempts - (exam.attempts_used || 0)} intento(s). Aprobación: ≥{exam.passing_score}%.</span>
                </div>
              )}
              <div className="space-y-6">
                {exam.questions.map((q, i) => (
                  <div key={i} data-testid={`exam-q-${i}`} className="border-b border-gris/20 pb-4 last:border-0">
                    <div className="font-medium text-marino mb-3">{i + 1}. {q.question}</div>
                    <div className="space-y-2">
                      {q.options.map((op, j) => (
                        <label key={j} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${answers[i] === j ? "border-naranja bg-naranja/5" : "border-gris/40 hover:border-marino"}`}>
                          <input data-testid={`exam-q-${i}-opt-${j}`} type="radio" name={`q-${i}`} checked={answers[i] === j} onChange={() => setAnswers({ ...answers, [i]: j })} />
                          <span className="text-marino text-sm">{op}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={submitExam}
                data-testid="exam-submit"
                disabled={Object.keys(answers).length !== exam.questions.length}
                className="mt-6 w-full bg-naranja text-white py-3 rounded-lg font-semibold hover:bg-naranjaHover disabled:opacity-50"
              >
                Enviar respuestas
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const needInitial = !enr.initial_exam_done;
  const finalBlocked = enr.status === "failed";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:grid lg:grid-cols-4 lg:gap-6">
      <ProtectionOverlay visible={devtoolsOpen} />
      {/* Sidebar */}
      <aside className="lg:col-span-1 mb-4 lg:mb-0">
        <div className="bg-white border border-gris/30 rounded-xl overflow-hidden sticky top-20">
          <button onClick={() => setDrawerOpen(!drawerOpen)} className="w-full flex items-center justify-between p-4 bg-marino text-white lg:cursor-default">
            <span className="font-heading font-bold text-sm truncate">{course.title}</span>
            <span className="lg:hidden">{drawerOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
          </button>
          <div className={`${drawerOpen ? "block" : "hidden lg:block"} max-h-[70vh] overflow-y-auto`}>
            <button
              disabled={enr.initial_exam_done}
              onClick={() => openExam("initial")}
              data-testid="btn-initial-exam"
              className={`w-full text-left p-3 border-b border-gris/20 flex items-center gap-2 text-sm ${enr.initial_exam_done ? "text-green-700" : "hover:bg-marino/5 text-marino"}`}
            >
              {enr.initial_exam_done ? <CheckCircle2 className="w-4 h-4" /> : <PlayCircle className="w-4 h-4 text-naranja" />}
              Examen diagnóstico {enr.initial_exam_done && `(${enr.initial_exam_score}%)`}
            </button>

            {course.lessons.map((l, i) => {
              const isDone = completed.has(l.id);
              const locked = needInitial;
              const mod = course.modules?.find((m) => m.id === l.module_id);
              const prev = i > 0 ? course.lessons[i - 1] : null;
              const showHeader = !prev || prev.module_id !== l.module_id;
              // Compute module progress
              let modProgress = null;
              if (showHeader) {
                const modLessons = course.lessons.filter((x) => x.module_id === l.module_id);
                const doneCount = modLessons.filter((x) => completed.has(x.id)).length;
                modProgress = { done: doneCount, total: modLessons.length, pct: Math.round((doneCount / modLessons.length) * 100) };
              }
              return (
                <div key={l.id}>
                  {showHeader && (
                    <div data-testid={`module-header-${mod?.id || "general"}`} className="px-3 pt-3 pb-2 bg-marino/5 border-b border-gris/20">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-xs font-heading font-bold uppercase tracking-wider text-marino/70 truncate">{mod?.title || "General"}</span>
                        <span data-testid={`module-progress-${mod?.id || "general"}`} className={`text-[10px] font-bold flex-shrink-0 ${modProgress.pct === 100 ? "text-green-700" : "text-marino/70"}`}>
                          {modProgress.done}/{modProgress.total}
                        </span>
                      </div>
                      <div className="h-1 bg-gris/25 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${modProgress.pct === 100 ? "bg-green-600" : "bg-naranja"}`}
                          style={{ width: `${modProgress.pct}%` }}
                        />
                      </div>
                    </div>
                  )}
                  <button
                    disabled={locked}
                    onClick={() => setActiveLesson(l)}
                    data-testid={`lesson-${l.id}`}
                    className={`w-full text-left p-3 border-b border-gris/20 flex items-center gap-2 text-sm ${activeLesson?.id === l.id ? "bg-naranja/10" : "hover:bg-marino/5"} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {locked ? <Lock className="w-4 h-4 text-gris" /> : isDone ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <PlayCircle className="w-4 h-4 text-marino/60" />}
                    <span className="text-marino">{i + 1}. {l.title}</span>
                  </button>
                </div>
              );
            })}

            <button
              disabled={!allDone || finalBlocked || enr.status === "completed"}
              onClick={() => openExam("final")}
              data-testid="btn-final-exam"
              className={`w-full text-left p-3 flex items-center gap-2 text-sm ${(!allDone || finalBlocked || enr.status === "completed") ? "opacity-50 cursor-not-allowed text-gris" : "hover:bg-marino/5 text-marino"}`}
            >
              <Award className="w-4 h-4 text-naranja" />
              Examen final {enr.status === "completed" && "✓"} {finalBlocked && "(Reprobado)"}
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="lg:col-span-3">
        {needInitial ? (
          <div className="bg-white border border-gris/30 rounded-xl p-8 text-center animate-fade-in">
            <Lock className="w-12 h-12 text-naranja mx-auto" />
            <h2 className="font-heading font-bold text-2xl text-marino mt-3">Curso bloqueado</h2>
            <p className="mt-2 text-marino/70">Debes realizar el examen diagnóstico (1 intento) para desbloquear el contenido.</p>
            <button data-testid="start-initial-exam" onClick={() => openExam("initial")} className="mt-5 bg-naranja text-white px-6 py-3 rounded-lg font-semibold hover:bg-naranjaHover">
              Iniciar examen diagnóstico
            </button>
          </div>
        ) : activeLesson ? (
          <div className="animate-fade-in space-y-4">
            <div className="bg-white border border-gris/30 rounded-xl overflow-hidden">
              {activeLesson.video_url && (
                <div className="aspect-video bg-black protected-video" onContextMenu={(e) => e.preventDefault()}>
                  <iframe title={activeLesson.title} src={activeLesson.video_url} className="w-full h-full" allowFullScreen />
                </div>
              )}
              <div className="p-5 sm:p-6">
                <h2 className="font-heading font-bold text-2xl text-marino">{activeLesson.title}</h2>
                <p className="mt-3 text-marino/80 leading-relaxed">{activeLesson.content}</p>
                {!completed.has(activeLesson.id) ? (
                  <button data-testid="mark-complete-btn" onClick={markComplete} className="mt-5 bg-naranja text-white px-5 py-2.5 rounded-lg font-medium hover:bg-naranjaHover">
                    Marcar como completado
                  </button>
                ) : (
                  <div className="mt-5 inline-flex items-center gap-2 text-green-700 font-medium"><CheckCircle2 className="w-5 h-5" /> Lección completada</div>
                )}
              </div>
            </div>

            {/* Ask professor */}
            <div className="bg-white border border-gris/30 rounded-xl p-5">
              <h3 className="font-heading font-bold text-marino flex items-center gap-2"><MessageSquare className="w-5 h-5 text-naranja" /> Preguntar al instructor</h3>
              <form onSubmit={askProfessor} className="mt-3 space-y-2">
                <textarea data-testid="ask-professor-input" value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} className="w-full border border-gris rounded-lg p-3 focus:border-marino focus:outline-none text-sm" placeholder="Escribe tu pregunta sobre esta lección..." />
                {askMsg && <div className="text-sm text-marino/70">{askMsg}</div>}
                <button disabled={sendingQ} data-testid="ask-professor-submit" className="bg-marino text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-marino/90 disabled:opacity-60">
                  {sendingQ ? "Enviando..." : "Enviar pregunta"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="p-10 text-marino/70">Selecciona una lección</div>
        )}
      </div>
    </div>
  );
}
